/**
 * @file src/offline/offlineEntryUrl.js
 * @description The address a downloaded collection is opened at later, with everything that only
 * made sense at the moment of downloading removed.
 *
 * A download is started from a URL somebody is already standing on, and that URL carries the
 * circumstances of the visit as well as the destination: which room they were in, that they arrived
 * from a host page, which seat the link dealt them. Stored verbatim and reopened a week later with
 * no connection, every one of those is a request the app will make and nothing can answer — it
 * would boot into a waiting room for a room that ended, holding a grant no relay will honour, and
 * report that offline is broken when what is broken is the address.
 *
 * So the stored address is rebuilt rather than copied: the collection, and the small set of things
 * that describe how to RENDER it, and nothing else. That is a deliberate allowlist and not a
 * blocklist — the parameters this app understands grow, every new one arrives without anybody
 * thinking about this file, and a blocklist would silently keep each of them.
 */

/**
 * What survives into a stored entry URL.
 *
 * `collection` is the destination. The rest are display choices a person made and would be annoyed
 * to lose: which graphics tier they play at, their language, whether the React shell is on. All of
 * them are answerable locally — none is a claim about a server.
 *
 * Deliberately absent, and why each: `room`, `roomId`, `joinCode`, `decks`, `players` (a room is a
 * live thing and there is nothing to join); `source`, `host`, `embed` (there is no host page in a
 * cold boot); `hydrationLevel` (the manifest decides what is hydrated, not the link); any auth or
 * grant token (unverifiable, and stale by the time it is read).
 */
const KEPT_PARAMS = ['collection', 'graphics', 'lang', 'ui', 'stretchEngine', 'offlineDownloads'];

/**
 * Build the address a downloaded collection should be reopened at.
 *
 * @param {string} collectionId the collection this entry is for — always written, never taken from
 *   the source URL, so a stored entry cannot point at a different collection than the one it holds.
 * @param {string|URL|null} [sourceUrl] the URL the download was started from. Absent → just the
 *   collection, on the app's own origin.
 * @returns {string|null} a path + query, relative to the app's origin. Relative on purpose: the
 *   origin can legitimately differ between the download and the boot (a dev host, a local run), and
 *   an absolute URL stored today is a link to somewhere else tomorrow.
 */
export function buildOfflineEntryUrl(collectionId, sourceUrl = null) {
  const id = typeof collectionId === 'string' ? collectionId.trim() : '';
  if (!id) return null;

  const params = new URLSearchParams();
  params.set('collection', id);

  if (sourceUrl) {
    let source;
    try {
      source = sourceUrl instanceof URL ? sourceUrl : new URL(String(sourceUrl), 'http://localhost');
    } catch {
      source = null;
    }
    if (source) {
      for (const name of KEPT_PARAMS) {
        // `collection` is already set from the argument and must not be overwritten by the link.
        if (name === 'collection') continue;
        const value = source.searchParams.get(name);
        if (value !== null && value !== '') params.set(name, value);
      }
    }
  }

  return `/?${params.toString()}`;
}

/** The allowlist, exported so a test can assert what is kept rather than restate it. */
export const OFFLINE_ENTRY_KEPT_PARAMS = KEPT_PARAMS;
